import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  Alert,
  AlertTitle,
  AlertDescription,
  useIsMobile,
} from '@design-system';

import { AlertTriangle, Mail } from 'lucide-react';

import { useState } from 'react';
import { EMAIL_WARNING_DISMISSED_KEY, useInviteClientStore } from '@/features/invite-client';
import { PilotRestricted } from '@/shared/components/pilot-restricted';

function EmailDeliverabilityWarningContent() {
  const {
    setShowEmailWarningModal,
    handleSendEmailAnyway,
    handleConnectEmail,
    generateShareableLink,
    inviteClientConfig,
    isSendingToExistingClient,
  } = useInviteClientStore();

  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const intakeFormLink = generateShareableLink();
  const emailTextContent = `${inviteClientConfig.emailBody}\n\n${intakeFormLink}`;

  function handleClose() {
    if (dontShowAgain) {
      localStorage.setItem(EMAIL_WARNING_DISMISSED_KEY, 'true');
    }
    setShowEmailWarningModal(false);
  }

  function handleSendAnyway() {
    if (dontShowAgain) {
      localStorage.setItem(EMAIL_WARNING_DISMISSED_KEY, 'true');
    }
    handleSendEmailAnyway(inviteClientConfig.isBulkMode);
  }

  async function handleCopyLink() {
    try {
      await navigator.clipboard.writeText(intakeFormLink);

      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  }

  async function handleCopyEmail() {
    try {
      await navigator.clipboard.writeText(emailTextContent);

      setCopiedEmail(true);
      setTimeout(() => setCopiedEmail(false), 2000);
    } catch (err) {
      console.error('Failed to copy email:', err);
    }
  }

  return (
    <>
      <div className="space-y-6">
        <Alert variant="warning">
          <AlertTriangle />
          <AlertTitle>Your email may end up in spam</AlertTitle>
          <AlertDescription>
            Without a connected email account, invitations are sent from a generic system email
            address. This significantly increases the chance of your email being marked as spam by
            email providers.
            <PilotRestricted
              capability="email-connection"
              hidden
            >
              <Button
                onClick={handleConnectEmail}
                className="mt-4 w-full"
              >
                <Mail />
                Connect Email Now
              </Button>
            </PilotRestricted>
          </AlertDescription>
        </Alert>

        {/* Not when the invitation is going to a client who already exists: the link copied here
            is the advisor's general one, and a form finished through it makes a new client. */}
        {!isSendingToExistingClient && (
          <Accordion
            type="single"
            className="w-full"
            collapsible
          >
            <AccordionItem
              value="alternative-method"
              className="data-[state=open]:bg-transparent"
            >
              <AccordionTrigger className="items-start text-sm [&>svg]:size-4">
                Alternatively, send it manually
              </AccordionTrigger>
              <AccordionContent className="flex flex-col gap-4 border-t pt-4">
                <p className="text-sm text-gray-700">
                  Copy the intake form link or email text below and send it manually.
                </p>

                <div>
                  <div className="mb-2 flex items-center gap-1">
                    <label className="text-sm font-medium text-gray-950">Email Template</label>
                    <Button
                      onClick={handleCopyEmail}
                      size="sm"
                      variant="outline"
                      className="ml-auto h-7 rounded-sm px-2 text-xs"
                    >
                      {copiedEmail ? 'Copied' : 'Copy Email Template'}
                    </Button>
                    <Button
                      onClick={handleCopyLink}
                      size="sm"
                      variant="outline"
                      className="h-7 rounded-sm px-2 text-xs"
                    >
                      {copiedLink ? 'Copied' : 'Copy Intake Form Link'}
                    </Button>
                  </div>
                  <div className="bg-muted text-foreground max-h-60 cursor-default overflow-y-auto whitespace-pre-wrap rounded-md border p-3 text-sm">
                    {emailTextContent}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}

        <div className="mb-6 flex items-center gap-2 md:mb-2">
          <Checkbox
            id="dontShowAgain"
            checked={dontShowAgain}
            onCheckedChange={checked => setDontShowAgain(checked as boolean)}
          />
          <label
            htmlFor="dontShowAgain"
            className="cursor-pointer text-sm text-gray-700"
          >
            Don't show this warning again
          </label>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <Button
          onClick={handleSendAnyway}
          variant="outline"
          className="w-full"
        >
          Send Anyway
        </Button>
        <Button
          onClick={handleClose}
          variant="ghost"
          className="w-full"
        >
          Cancel
        </Button>
      </div>
    </>
  );
}

export function EmailDeliverabilityWarningModal() {
  const { showEmailWarningModal, setShowEmailWarningModal } = useInviteClientStore();
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Drawer
        open={showEmailWarningModal}
        onOpenChange={setShowEmailWarningModal}
      >
        <DrawerContent className="z-60 max-h-[90vh]! overflow-y-auto px-4">
          <DrawerHeader className="text-left">
            <DrawerTitle>Email Deliverability Warning</DrawerTitle>
            <DrawerDescription>Your email may not reach your client's inbox</DrawerDescription>
          </DrawerHeader>
          <EmailDeliverabilityWarningContent />
          <DrawerFooter />
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog
      open={showEmailWarningModal}
      onOpenChange={setShowEmailWarningModal}
    >
      <DialogContent className="z-60 max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Email Deliverability Warning</DialogTitle>
          <DialogDescription>Your email may not reach your client's inbox</DialogDescription>
        </DialogHeader>
        <EmailDeliverabilityWarningContent />
      </DialogContent>
    </Dialog>
  );
}
